/**
 * The client half of the AI photo splitter.
 *
 * The cutting itself happens on the server — the model is asked where the prints
 * are and the pieces come back already cut, at the source picture's resolution.
 * What is left here is reading the file in, and writing the pieces back out in
 * whichever format was asked for.
 */

import { encodeCanvas } from '../shared/imageOutputFormats';

export const AI_SPLIT_ACCEPT = 'image/png,image/jpeg,image/webp';
export const AI_SPLIT_MAX_BYTES = 20 * 1024 * 1024;

/** A picked file as the data URL the API takes. */
export function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('read failed'));
        reader.readAsDataURL(file);
    });
}

/**
 * One returned piece, encoded as the chosen format.
 *
 * The server sends JPEG, so "original" means "leave it exactly as it came" — no
 * re-encode at all. Every other format goes through the shared image encoder the
 * free tools use, which is also what makes AVIF possible: Chrome's canvas cannot
 * write it, and that encoder falls back to the Squoosh WebAssembly one.
 *
 * The extension comes back WITH the blob rather than being derived from the
 * requested format, because the encoder is allowed to land somewhere else: AVIF
 * falls back to WebP when the WebAssembly module cannot load, and a WebP named
 * `.avif` is a file that will not open.
 *
 * @returns {Promise<{ blob: Blob, ext: string }>}
 */
export async function pieceToBlob(dataUrl, format) {
    const blob = await (await fetch(dataUrl)).blob();
    if (format === 'original') return { blob, ext: 'jpg' };

    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0);
    bitmap.close?.();
    // `encodeCanvas` answers with { blob, format, ext } — handing that object
    // straight to a download is what made "change the format, then download"
    // fail with nothing but an error toast.
    const encoded = await encodeCanvas(canvas, format);
    return { blob: encoded.blob, ext: encoded.ext };
}
